import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
} from '@tanstack/react-query';
import {
  Campaign,
  CampaignCheck,
  CampaignFilter,
  CampaignProgress,
  CampaignCheckCompletion,
  CampaignMembershipEntry,
  CampaignExemption,
  EntityCampaignSummary,
  FilterPredicate,
  FilterFieldOptions,
  FilterPreviewResult,
  RubricCheck,
  RubricCheckOwnership,
} from '@internal/backstage-plugin-bidone-backend';
import { UseQueryCustomResult } from '../types';
import { useAuthedFetch } from './useAuthedFetch';

const CAMPAIGNS_KEY = ['campaigns'] as const;
const campaignKey = (id: string) => ['campaign', id] as const;
const FILTERS_KEY = ['filters'] as const;
const filterKey = (id: string) => ['filter', id] as const;
const RUBRIC_CHECKS_KEY = ['rubricChecks'] as const;
const RUBRIC_OWNERSHIP_KEY = ['rubricCheckOwnership'] as const;

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export const useCampaigns = (): UseQueryCustomResult<Campaign[]> => {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: CAMPAIGNS_KEY,
    queryFn: async () => authedFetch('/campaigns'),
  });
};

export const useCampaign = (
  id: string | null,
): UseQueryCustomResult<Campaign> => {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: campaignKey(id ?? ''),
    queryFn: async () =>
      authedFetch(`/campaigns/${encodeURIComponent(id as string)}`),
    enabled: !!id,
  });
};

export const useFilters = (): UseQueryCustomResult<CampaignFilter[]> => {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: FILTERS_KEY,
    queryFn: async () => authedFetch('/filters'),
  });
};

export const useFilter = (
  id: string | null,
): UseQueryCustomResult<CampaignFilter> => {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: filterKey(id ?? ''),
    queryFn: async () =>
      authedFetch(`/filters/${encodeURIComponent(id as string)}`),
    enabled: !!id,
  });
};

export const useRubricChecks = (): UseQueryCustomResult<RubricCheck[]> => {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: RUBRIC_CHECKS_KEY,
    queryFn: async () => authedFetch('/rubric-checks'),
  });
};

// Which rubric checks are currently owned by an active campaign. Used by the
// scorecards pages to hide them — a check belongs to one owner at a time.
export const useRubricCheckOwnership = (): UseQueryCustomResult<
  RubricCheckOwnership[]
> => {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: RUBRIC_OWNERSHIP_KEY,
    queryFn: async () => authedFetch('/rubric-checks/ownership'),
  });
};

// Field + value autocomplete data for the filter builder. The backend caches
// the underlying snapshot, but it's still tag/topology-grade data — 15 min.
export const useFilterOptions = (): UseQueryCustomResult<FilterFieldOptions> => {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: ['filterOptions'],
    queryFn: async () => authedFetch('/filters/options'),
    staleTime: 15 * 60 * 1000,
  });
};

export type FilterPreviewInput = {
  combine: 'all' | 'any';
  predicates: FilterPredicate[];
};

// Live "Matches N components" count for the builder. Keyed on the serialized
// filter so edits re-run it; previous data is kept to avoid count flicker
// while typing.
export const useFilterPreview = (
  input: FilterPreviewInput | null,
): UseQueryCustomResult<FilterPreviewResult> => {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: ['filterPreview', JSON.stringify(input)],
    queryFn: async () =>
      authedFetch('/filters/preview', { method: 'POST', json: input }),
    enabled: !!input,
    placeholderData: previous => previous,
  });
};

// ---------------------------------------------------------------------------
// Campaign mutations
// ---------------------------------------------------------------------------

export type CreateCampaignInput = { name: string; ownerEntityRef?: string };

export const useCreateCampaign = (): UseMutationResult<
  Campaign,
  Error,
  CreateCampaignInput
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCampaignInput) =>
      authedFetch('/campaigns', { method: 'POST', json: input }),
    onSuccess: data => {
      qc.setQueryData(campaignKey(data.id), data);
      qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY });
    },
  });
};

export type UpdateCampaignInput = {
  id: string;
  patch: Partial<
    Pick<
      Campaign,
      | 'name'
      | 'ownerEntityRef'
      | 'brief'
      | 'filterId'
      | 'startDate'
      | 'targetDate'
      | 'membershipMode'
    >
  >;
};

export const useUpdateCampaign = (): UseMutationResult<
  Campaign,
  Error,
  UpdateCampaignInput
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: UpdateCampaignInput) =>
      authedFetch(`/campaigns/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        json: patch,
      }),
    onSuccess: data => {
      qc.setQueryData(campaignKey(data.id), data);
      qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY });
    },
  });
};

export const useDeleteCampaign = (): UseMutationResult<
  unknown,
  Error,
  { id: string }
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }) =>
      authedFetch(`/campaigns/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: (_data, { id }) => {
      qc.removeQueries({ queryKey: campaignKey(id) });
      qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY });
    },
  });
};

// Lifecycle transitions all return the updated campaign, so they share one
// implementation. Ownership can change on schedule/end, so the rubric queries
// are invalidated too.
const useCampaignTransition = (
  action: 'schedule' | 'unschedule' | 'end',
): UseMutationResult<
  Campaign,
  Error,
  { id: string; migratedCheckIds?: string[] }
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, migratedCheckIds }) =>
      authedFetch(`/campaigns/${encodeURIComponent(id)}/${action}`, {
        method: 'POST',
        json: action === 'end' ? { migratedCheckIds } : {},
      }),
    onSuccess: data => {
      qc.setQueryData(campaignKey(data.id), data);
      qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY });
      qc.invalidateQueries({ queryKey: RUBRIC_CHECKS_KEY });
      qc.invalidateQueries({ queryKey: RUBRIC_OWNERSHIP_KEY });
    },
  });
};

export const useScheduleCampaign = () => useCampaignTransition('schedule');
export const useUnscheduleCampaign = () => useCampaignTransition('unschedule');
export const useEndCampaign = () => useCampaignTransition('end');

// ---------------------------------------------------------------------------
// Campaign check mutations — responses are single checks, so the campaign
// query is invalidated rather than patched.
// ---------------------------------------------------------------------------

export type CheckInput = Omit<CampaignCheck, 'id' | 'campaignId'>;

export const useAddCampaignCheck = (): UseMutationResult<
  CampaignCheck,
  Error,
  { campaignId: string; input: CheckInput }
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, input }) =>
      authedFetch(`/campaigns/${encodeURIComponent(campaignId)}/checks`, {
        method: 'POST',
        json: input,
      }),
    onSuccess: (_data, { campaignId }) => {
      qc.invalidateQueries({ queryKey: campaignKey(campaignId) });
    },
  });
};

export const useUpdateCampaignCheck = (): UseMutationResult<
  CampaignCheck,
  Error,
  { campaignId: string; checkId: string; patch: Partial<CheckInput> }
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, checkId, patch }) =>
      authedFetch(
        `/campaigns/${encodeURIComponent(campaignId)}/checks/${encodeURIComponent(checkId)}`,
        { method: 'PATCH', json: patch },
      ),
    onSuccess: (_data, { campaignId }) => {
      qc.invalidateQueries({ queryKey: campaignKey(campaignId) });
    },
  });
};

export const useDeleteCampaignCheck = (): UseMutationResult<
  unknown,
  Error,
  { campaignId: string; checkId: string }
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, checkId }) =>
      authedFetch(
        `/campaigns/${encodeURIComponent(campaignId)}/checks/${encodeURIComponent(checkId)}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, { campaignId }) => {
      qc.invalidateQueries({ queryKey: campaignKey(campaignId) });
      qc.invalidateQueries({ queryKey: RUBRIC_CHECKS_KEY });
      qc.invalidateQueries({ queryKey: RUBRIC_OWNERSHIP_KEY });
    },
  });
};

export const useCopyFromRubric = (): UseMutationResult<
  CampaignCheck[],
  Error,
  { campaignId: string; rubricCheckIds: string[] }
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, rubricCheckIds }) =>
      authedFetch(
        `/campaigns/${encodeURIComponent(campaignId)}/checks/copy-from-rubric`,
        { method: 'POST', json: { rubricCheckIds } },
      ),
    onSuccess: (_data, { campaignId }) => {
      qc.invalidateQueries({ queryKey: campaignKey(campaignId) });
      qc.invalidateQueries({ queryKey: RUBRIC_CHECKS_KEY });
      qc.invalidateQueries({ queryKey: RUBRIC_OWNERSHIP_KEY });
    },
  });
};

// ---------------------------------------------------------------------------
// Filter mutations
// ---------------------------------------------------------------------------

export type CreateFilterInput = {
  name: string;
  combine?: 'all' | 'any';
  predicates?: FilterPredicate[];
};

export const useCreateFilter = (): UseMutationResult<
  CampaignFilter,
  Error,
  CreateFilterInput
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateFilterInput) =>
      authedFetch('/filters', { method: 'POST', json: input }),
    onSuccess: data => {
      qc.setQueryData(filterKey(data.id), data);
      qc.invalidateQueries({ queryKey: FILTERS_KEY });
    },
  });
};

export type UpdateFilterInput = {
  id: string;
  patch: Partial<Pick<CampaignFilter, 'name' | 'combine' | 'predicates'>>;
};

export const useUpdateFilter = (): UseMutationResult<
  CampaignFilter,
  Error,
  UpdateFilterInput
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: UpdateFilterInput) =>
      authedFetch(`/filters/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        json: patch,
      }),
    onSuccess: data => {
      qc.setQueryData(filterKey(data.id), data);
      qc.invalidateQueries({ queryKey: FILTERS_KEY });
    },
  });
};

export const useDeleteFilter = (): UseMutationResult<
  unknown,
  Error,
  { id: string }
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }) =>
      authedFetch(`/filters/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: (_data, { id }) => {
      qc.removeQueries({ queryKey: filterKey(id) });
      qc.invalidateQueries({ queryKey: FILTERS_KEY });
    },
  });
};

// ---------------------------------------------------------------------------
// Phase 4 — progress, manual-check completions, snapshot membership,
// exemptions, and the per-entity view.
// ---------------------------------------------------------------------------

const progressKey = (id: string) => ['campaignProgress', id] as const;
const membershipKey = (id: string) => ['campaignMembership', id] as const;
const exemptionsKey = (id: string) => ['campaignExemptions', id] as const;
const entityCampaignsKey = (ref: string) => ['entityCampaigns', ref] as const;

// The evaluated components-x-checks matrix. Live state (no staleTime) so it
// reflects attestations/exemptions immediately.
export const useCampaignProgress = (
  id: string | null,
): UseQueryCustomResult<CampaignProgress> => {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: progressKey(id ?? ''),
    queryFn: async () =>
      authedFetch(`/campaigns/${encodeURIComponent(id as string)}/progress`),
    enabled: !!id,
  });
};

// Campaigns targeting a given component, with per-check status — powers the
// EntityCampaignsTab.
export const useEntityCampaigns = (
  ref: string | null,
): UseQueryCustomResult<EntityCampaignSummary[]> => {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: entityCampaignsKey(ref ?? ''),
    queryFn: async () =>
      authedFetch(`/entity-campaigns?ref=${encodeURIComponent(ref as string)}`),
    enabled: !!ref,
  });
};

// Attest (or clear) a manual check for one component. Invalidates both the
// campaign matrix and the component's entity view.
export const useSetCheckCompletion = (): UseMutationResult<
  CampaignCheckCompletion,
  Error,
  {
    campaignId: string;
    checkId: string;
    componentRef: string;
    done: boolean;
    note?: string;
  }
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, checkId, componentRef, done, note }) =>
      authedFetch(
        `/campaigns/${encodeURIComponent(campaignId)}/checks/${encodeURIComponent(
          checkId,
        )}/completion`,
        { method: 'POST', json: { componentRef, done, note } },
      ),
    onSuccess: (_data, { campaignId, componentRef }) => {
      qc.invalidateQueries({ queryKey: progressKey(campaignId) });
      qc.invalidateQueries({ queryKey: entityCampaignsKey(componentRef) });
    },
  });
};

export const useCampaignMembership = (
  id: string | null,
): UseQueryCustomResult<CampaignMembershipEntry[]> => {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: membershipKey(id ?? ''),
    queryFn: async () =>
      authedFetch(`/campaigns/${encodeURIComponent(id as string)}/membership`),
    enabled: !!id,
  });
};

// Curate: replace the frozen cohort with the checklist result.
export const useFreezeMembership = (): UseMutationResult<
  CampaignMembershipEntry[],
  Error,
  { campaignId: string; refs: string[] }
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, refs }) =>
      authedFetch(
        `/campaigns/${encodeURIComponent(campaignId)}/membership/freeze`,
        { method: 'POST', json: { refs } },
      ),
    onSuccess: (_data, { campaignId }) => {
      qc.invalidateQueries({ queryKey: membershipKey(campaignId) });
      qc.invalidateQueries({ queryKey: progressKey(campaignId) });
    },
  });
};

export const useAddMember = (): UseMutationResult<
  CampaignMembershipEntry,
  Error,
  { campaignId: string; componentRef: string }
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, componentRef }) =>
      authedFetch(`/campaigns/${encodeURIComponent(campaignId)}/membership`, {
        method: 'POST',
        json: { componentRef },
      }),
    onSuccess: (_data, { campaignId }) => {
      qc.invalidateQueries({ queryKey: membershipKey(campaignId) });
      qc.invalidateQueries({ queryKey: progressKey(campaignId) });
    },
  });
};

export const useRemoveMember = (): UseMutationResult<
  unknown,
  Error,
  { campaignId: string; componentRef: string }
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, componentRef }) =>
      authedFetch(
        `/campaigns/${encodeURIComponent(campaignId)}/membership/remove`,
        { method: 'POST', json: { componentRef } },
      ),
    onSuccess: (_data, { campaignId }) => {
      qc.invalidateQueries({ queryKey: membershipKey(campaignId) });
      qc.invalidateQueries({ queryKey: progressKey(campaignId) });
    },
  });
};

export const useCampaignExemptions = (
  id: string | null,
): UseQueryCustomResult<CampaignExemption[]> => {
  const authedFetch = useAuthedFetch();
  return useQuery({
    queryKey: exemptionsKey(id ?? ''),
    queryFn: async () =>
      authedFetch(`/campaigns/${encodeURIComponent(id as string)}/exemptions`),
    enabled: !!id,
  });
};

export const useAddExemption = (): UseMutationResult<
  CampaignExemption,
  Error,
  {
    campaignId: string;
    componentRef: string;
    checkId?: string | null;
    reason?: string;
  }
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, componentRef, checkId, reason }) =>
      authedFetch(`/campaigns/${encodeURIComponent(campaignId)}/exemptions`, {
        method: 'POST',
        json: { componentRef, checkId: checkId ?? null, reason: reason ?? '' },
      }),
    onSuccess: (_data, { campaignId }) => {
      qc.invalidateQueries({ queryKey: exemptionsKey(campaignId) });
      qc.invalidateQueries({ queryKey: progressKey(campaignId) });
    },
  });
};

export const useRemoveExemption = (): UseMutationResult<
  unknown,
  Error,
  { campaignId: string; componentRef: string; checkId?: string | null }
> => {
  const authedFetch = useAuthedFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, componentRef, checkId }) =>
      authedFetch(
        `/campaigns/${encodeURIComponent(campaignId)}/exemptions/remove`,
        { method: 'POST', json: { componentRef, checkId: checkId ?? null } },
      ),
    onSuccess: (_data, { campaignId }) => {
      qc.invalidateQueries({ queryKey: exemptionsKey(campaignId) });
      qc.invalidateQueries({ queryKey: progressKey(campaignId) });
    },
  });
};
